import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';

import CustomModal from '@/components/CustomModal';
import Input from '@/components/Input/Input';
import SectionLoader from '@/components/Loader/SectionLoader';
import Select from '@/components/Select';
import TextEditor from '@/components/TextEditor';
import { FAQ_USER_OPTIONS } from '@/constants/constant';
import { ERoleAccess, ECommonStatus } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { addFaqSchema, TAddFaqFormValues } from '@/schema/addFaqSchema';
import { addFAQ, updateFAQ, getFAQById } from '@/service/editorialService';
import { IAddFaqPayload } from '@/types/payload';
import { zodResolver } from '@hookform/resolvers/zod';

interface IAddEditFaqModalProps {
  setIsOpen: (isOpen: boolean) => void;
  refetch: () => void;
  selectedFaqId?: string | null;
}

const DEFAULT_VALUES: TAddFaqFormValues = {
  question: '',
  answer: '',
  user_type: [],
};

const AddEditFaqModal = ({
  setIsOpen,
  refetch,
  selectedFaqId,
}: IAddEditFaqModalProps) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);

  const form = useForm<TAddFaqFormValues>({
    resolver: zodResolver(addFaqSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    control,
    formState: { errors },
    watch,
  } = form;

  const selectedUserType = watch('user_type');

  useEffect(() => {
    const fetchFaqData = async () => {
      if (selectedFaqId) {
        setFetchingData(true);
        try {
          const response = await getFAQById(selectedFaqId);

          if (response?.status && response?.data) {
            const faqData = response.data;
            reset({
              question: faqData.question || '',
              answer: faqData.answer || '',
              user_type: faqData.user_roles || [],
            });
          } else {
            console.error('Error fetching FAQ:', response?.message);
          }
        } catch (error) {
          console.error('Error fetching FAQ:', error);
        } finally {
          setFetchingData(false);
        }
      } else {
        reset(DEFAULT_VALUES);
      }
    };

    fetchFaqData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFaqId]);

  // Clear form when modal closes
  useEffect(() => {
    return () => {
      reset(DEFAULT_VALUES);
      clearErrors();
    };
  }, [reset, clearErrors]);

  const onSubmit = async (data: TAddFaqFormValues) => {
    setLoading(true);

    try {
      const payload: IAddFaqPayload = {
        question: data.question,
        answer: data.answer,
        status: ECommonStatus.ACTIVE,
        user_roles: data.user_type,
      };

      let response;

      if (selectedFaqId) {
        // Update existing FAQ
        response = await updateFAQ(selectedFaqId, payload);
      } else {
        // Add new FAQ
        response = await addFAQ(payload);
      }

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        refetch();
        setIsOpen(false);
      } else {
        console.error('Error response:', response?.message);
      }
    } catch (error) {
      console.error('Error saving FAQ:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess(ERoleAccess.EDITORIAL_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      heading={
        selectedFaqId
          ? formatText('editorial.faq.modal.edit-title')
          : formatText('editorial.faq.modal.add-title')
      }
      isFooter
      position="right"
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        desructive: true,
        onClick: () => setIsOpen(false),
        disabled: loading || fetchingData,
      }}
      confirmButtonProps={{
        label: selectedFaqId
          ? formatText('common.actions.save')
          : formatText('editorial.faq.actions.add-btn'),
        onClick: handleSubmit(onSubmit),
        loading: loading,
        disabled: fetchingData,
      }}
      className="md:w-150 w-full"
    >
      {fetchingData ? (
        <SectionLoader className="h-full flex justify-center" />
      ) : (
        <form className="w-full flex flex-col gap-4">
          <Input
            name="question"
            label={formatText('editorial.faq.modal.question-label')}
            placeholder={formatText('editorial.faq.modal.question-placeholder')}
            register={register}
            error={
              errors.question?.message
                ? formatText(errors.question?.message)
                : undefined
            }
            isRequired
            disabled={loading}
          />

          <Controller
            control={control}
            name="answer"
            render={({ field }) => (
              <TextEditor
                label={formatText('editorial.faq.modal.answer-label')}
                isRequired
                error={
                  errors.answer?.message
                    ? formatText(errors.answer?.message)
                    : undefined
                }
                value={field.value}
                onChange={field.onChange}
                placeholder={formatText(
                  'editorial.faq.modal.answer-placeholder'
                )}
                maxLength={500}
                isFullToolbar={false}
                disabled={loading}
                showCount={true}
              />
            )}
          />

          <Select
            name={formatText('editorial.faq.modal.user-type-label')}
            placeholder={formatText(
              'editorial.faq.modal.user-type-placeholder'
            )}
            options={FAQ_USER_OPTIONS}
            selected={selectedUserType}
            onChange={(val) =>
              setValue('user_type', Array.isArray(val) ? val : [], {
                shouldValidate: true,
              })
            }
            error={
              errors.user_type?.message
                ? formatText(errors.user_type?.message)
                : undefined
            }
            isRequired
            isTextFormat
            isMulti
            disabled={loading}
          />
        </form>
      )}
    </CustomModal>
  );
};

export default AddEditFaqModal;
